// ═══════════════════════════════════════════════════════════════════════════
// sponsorDeals.js — a brand user's self-serve sponsorship deals.
//
// Each deal is owned by a brand account (podcaster_profiles.account_type =
// 'brand'). RLS (migration 074) enforces: owners manage their own, everyone
// can read LIVE deals (host matching), admins can moderate any. No RPCs — the
// policies do the gating, so these are plain table reads/writes.
//
// Trust model: live-now. A new deal defaults to 'live' and is immediately
// matchable; the owner can pause/close it and admins can moderate.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';

const DEAL_COLUMNS = 'id, owner_id, title, tagline, category, logo, deal_type, budget, niches, min_eps, status, created_at, updated_at';

// Normalize a form payload into a clean DB row shape.
function cleanDeal(input = {}) {
  const niches = Array.isArray(input.niches)
    ? input.niches
    : String(input.niches || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  return {
    title:     String(input.title || '').trim().slice(0, 120),
    tagline:   String(input.tagline || '').trim().slice(0, 240) || null,
    category:  String(input.category || '').trim().slice(0, 80) || null,
    logo:      String(input.logo || '').trim().slice(0, 16) || null,
    deal_type: input.deal_type === 'free' ? 'free' : 'paid',
    budget:    String(input.budget || '').trim().slice(0, 60) || null,
    niches:    niches.slice(0, 12),
    min_eps:   Math.max(0, Number.parseInt(input.min_eps, 10) || 0),
    status:    ['draft', 'live', 'paused', 'closed'].includes(input.status) ? input.status : 'live',
  };
}

/** All of a brand's own deals, newest first. */
export async function listMyDeals(ownerId) {
  if (!supabase || !ownerId) return [];
  const { data, error } = await supabase
    .from('sponsor_deals')
    .select(DEAL_COLUMNS)
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: false });
  if (error) { console.error('[sponsorDeals] listMyDeals:', error.message); return []; }
  return data || [];
}

/** Create (no id) or update (with id) a deal. Returns the saved row. */
export async function saveDeal(ownerId, deal = {}) {
  if (!supabase || !ownerId) throw new Error('Not signed in.');
  const payload = cleanDeal(deal);
  if (!payload.title) throw new Error('Give the deal a title.');
  if (deal.id) {
    const { data, error } = await supabase
      .from('sponsor_deals')
      .update(payload)
      .eq('id', deal.id)
      .eq('owner_id', ownerId)
      .select(DEAL_COLUMNS)
      .single();
    if (error) throw new Error(error.message);
    return data;
  }
  const { data, error } = await supabase
    .from('sponsor_deals')
    .insert({ ...payload, owner_id: ownerId })
    .select(DEAL_COLUMNS)
    .single();
  if (error) throw new Error(error.message);
  return data;
}

/** Flip a deal's status (live / paused / closed / draft). */
export async function setDealStatus(ownerId, id, status) {
  if (!supabase || !ownerId || !id) throw new Error('Missing deal.');
  const s = ['draft', 'live', 'paused', 'closed'].includes(status) ? status : 'paused';
  const { data, error } = await supabase
    .from('sponsor_deals')
    .update({ status: s })
    .eq('id', id)
    .eq('owner_id', ownerId)
    .select(DEAL_COLUMNS)
    .single();
  if (error) throw new Error(error.message);
  return data;
}

/** Permanently delete a deal. */
export async function removeDeal(ownerId, id) {
  if (!supabase || !ownerId || !id) throw new Error('Missing deal.');
  const { error } = await supabase
    .from('sponsor_deals')
    .delete()
    .eq('id', id)
    .eq('owner_id', ownerId);
  if (error) throw new Error(error.message);
  return true;
}

/** Live deals across all brands, optionally filtered to a niche. Used by the
 *  host-facing Earn surface (Phase 2). Read-only; RLS allows only status=live. */
export async function listLiveDeals({ niche, limit = 24 } = {}) {
  if (!supabase) return [];
  let q = supabase
    .from('sponsor_deals')
    .select(DEAL_COLUMNS)
    .eq('status', 'live')
    .order('created_at', { ascending: false })
    .limit(limit);
  const { data, error } = await q;
  if (error) { console.error('[sponsorDeals] listLiveDeals:', error.message); return []; }
  const rows = data || [];
  if (!niche) return rows;
  const n = String(niche).toLowerCase();
  return rows.filter((d) =>
    (d.niches || []).some((tag) => {
      const t = String(tag).toLowerCase();
      return n.includes(t) || t.includes(n);
    })
  );
}
